import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import * as Sentry from "@sentry/react";

interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

interface LoginAttempts {
  count: number;
  lastAttempt: number;
  lockedUntil?: number;
}

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const PASSWORD_RESET_TIMEOUT = 60 * 60 * 1000; // 1 hour

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
    error: null
  });

  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const abortController = useRef<AbortController | null>(null);

  const [loginAttempts, setLoginAttempts] = useState<LoginAttempts>(() => {
    const stored = localStorage.getItem('loginAttempts');
    return stored ? JSON.parse(stored) : { count: 0, lastAttempt: 0 };
  });

  const updateState = useCallback((updates: Partial<AuthState>) => {
    if (mountedRef.current) {
      setState(prev => ({ ...prev, ...updates }));
    }
  }, []);

  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  useEffect(() => {
    localStorage.setItem('loginAttempts', JSON.stringify(loginAttempts));
  }, [loginAttempts]);

  useEffect(() => {
    if (loginAttempts.lockedUntil && Date.now() > loginAttempts.lockedUntil) {
      setLoginAttempts({ count: 0, lastAttempt: 0 });
    }
  }, [loginAttempts]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (session?.user) {
          const { data: adminData, error: adminError } = await supabase
            .from('admin_users')
            .select('id, email, is_active')
            .eq('email', session.user.email)
            .maybeSingle();

          if (adminError) throw adminError;

          const isAdmin = !!adminData?.is_active;

          updateState({
            isAuthenticated: true,
            isAdmin,
            isLoading: false,
            error: null
          });

          if (adminData?.id) {
            await supabase
              .from('admin_users')
              .update({ last_login: new Date().toISOString() })
              .eq('id', adminData.id);
          }

          Sentry.setUser({
            email: session.user.email,
            id: session.user.id,
            role: isAdmin ? 'admin' : 'user'
          });
        } else {
          updateState({ 
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false 
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        updateState({
          isLoading: false,
          error: 'Failed to initialize authentication'
        });
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mountedRef.current) return;

        if (event === 'SIGNED_OUT') {
          updateState({
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
            error: null
          });
          Sentry.setUser(null);
          navigate('/', { replace: true });
          return;
        }

        if (session?.user) {
          try {
            const { data: adminData, error: adminError } = await supabase
              .from('admin_users')
              .select('id, email, is_active')
              .eq('email', session.user.email)
              .maybeSingle();

            if (adminError) throw adminError;

            const isAdmin = !!adminData?.is_active;

            updateState({
              isAuthenticated: true,
              isAdmin,
              isLoading: false,
              error: null
            });

            if (adminData?.id) {
              await supabase
                .from('admin_users')
                .update({ last_login: new Date().toISOString() })
                .eq('id', adminData.id);
            }

            Sentry.setUser({
              email: session.user.email,
              id: session.user.id,
              role: isAdmin ? 'admin' : 'user'
            });
          } catch (error) {
            Sentry.captureException(error);
            updateState({
              error: 'Failed to verify admin status'
            });
          }
        }
      }
    );

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [navigate, updateState]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();

      const now = Date.now();

      if (loginAttempts.lockedUntil && now < loginAttempts.lockedUntil) {
        const remainingMinutes = Math.ceil(
          (loginAttempts.lockedUntil - now) / 1000 / 60
        );
        throw new Error(
          `Account temporarily locked. Please try again in ${remainingMinutes} minutes.`
        );
      }

      if (now - loginAttempts.lastAttempt > PASSWORD_RESET_TIMEOUT) {
        setLoginAttempts({ count: 0, lastAttempt: now });
      }

      updateState({ isLoading: true, error: null });

      const { data: adminUser, error: adminCheckError } = await supabase
        .from('admin_users')
        .select('id, is_active')
        .eq('email', email)
        .maybeSingle();

      if (adminCheckError) {
        throw new Error('Failed to verify admin status');
      }

      if (!adminUser) {
        throw new Error('Invalid credentials');
      }

      if (!adminUser.is_active) {
        throw new Error('This account has been deactivated');
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      }, { signal: abortController.current.signal });

      if (signInError) {
        throw signInError;
      }

      if (!data.user) {
        throw new Error('Authentication failed');
      }

      setLoginAttempts({ count: 0, lastAttempt: now });

      updateState({
        isAuthenticated: true,
        isAdmin: true,
        isLoading: false,
        error: null
      });

      return { success: true };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { success: false, error: 'Sign in cancelled' };
      }

      const now = Date.now();
      const newCount = loginAttempts.count + 1;
      
      setLoginAttempts({
        count: newCount,
        lastAttempt: now,
        lockedUntil: newCount >= MAX_LOGIN_ATTEMPTS ? now + LOCKOUT_DURATION : undefined
      });

      const message = error instanceof Error ? error.message : 'Authentication failed';
      
      updateState({ 
        isLoading: false,
        error: message 
      });
      
      Sentry.captureException(error);

      return { 
        success: false, 
        error: newCount >= MAX_LOGIN_ATTEMPTS 
          ? `Too many failed attempts. Account locked for ${LOCKOUT_DURATION / 1000 / 60} minutes.`
          : message
      };
    } finally {
      abortController.current = null;
    }
  }, [loginAttempts, updateState]);

  const signOut = useCallback(async () => {
    try {
      updateState({ isLoading: true });
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      updateState({
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        error: null
      });

      Sentry.setUser(null);

      const preserveKeys = ['dark-mode'];
      const keysToPreserve = preserveKeys.reduce((acc, key) => {
        const value = localStorage.getItem(key);
        if (value) acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

      localStorage.clear();

      Object.entries(keysToPreserve).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      Sentry.captureException(error);
      updateState({
        isLoading: false,
        error: 'Failed to sign out'
      });
    }
  }, [navigate, updateState]);

  return {
    ...state,
    signIn,
    signOut,
    clearError
  };
};